/**
 * Breadcrumbs Component
 *
 * Displays a breadcrumb trail for navigation.
 * Supports dynamic labels from NavigationContext for UUIDs/slugs.
 * Mobile-responsive with collapsing for deep paths.
 * Non-navigable intermediate paths render as plain text to avoid 404s.
 *
 * @module shared/components/layout/Breadcrumbs
 */

import React from "react";
import { ChevronRight, Home } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
} from "@/components/ui/breadcrumb";
import { useNavigation } from "@/contexts/NavigationContext";

// Static route names for known paths
const routeNames: Record<string, string> = {
  "/": "Dashboard",
  "/dashboard": "Dashboard",
  "/templates": "Templates",
  "/templates/conciliation": "Conciliation",
  "/templates/arbitration": "Arbitration",
  "/templates/suit-filing": "Suit Filing",
  "/businesses": "Businesses",
  "/courts": "Courts",
  "/letterhead-components": "Letterhead Components",
  "/arbitration/pipeline": "Pipeline",
  "/pipeline": "Pipeline",
  "/conciliation/dashboard": "Conciliation",
  "/conciliation/command-center": "Command Center",
  "/suit-categories": "Suit Categories",
  "/suit-filing/start": "Start Suit Filing",
  "/ocr-tool": "OCR Tool",
  "/calendar": "Calendar",
  "/settings/members": "Members",
  "/settings/business": "Business Settings",
  "/settings/signatures": "Signatures",
  "/workspaces": "Workspaces",
  "/assistants": "Assistants",
  "/assistants/create": "Start Arbitration Process",
  // Platform routes
  "/platform": "Platform",
  "/platform/firms": "Firms",
  "/platform/admins": "Platform Admins",
  "/platform/businesses": "All Businesses",
  "/platform/configuration": "Configuration",
  "/platform/queue": "Observability",
  "/platform/rate-limits": "Rate Limits",
  // Organization Hub
  "/organization": "Organization",
  "/firm/onboarding": "Onboarding",
  // Business Settings Hub
  "/business/settings": "Business Settings",
  // Letterhead
  "/letterhead-components/new": "Create",
};

// Pattern-based label generators for specific segments
const patternLabels: Record<string, (segment: string) => string> = {
  "variant": () => "Variant",
  "edit": () => "Details", // Neutral label, not action-oriented
  "new": () => "Create",
  "process": () => "Process",
  "business": () => "Business",
  "court": () => "Court",
  "region": () => "Region",
  "category": () => "Category",
  "job": () => "Job",
};

// Paths that have no standalone route — render as text, not links
const nonNavigablePaths = new Set([
  "/conciliation",
  "/conciliation/job",
  "/settings",
  "/suit-filing",
  "/suit-filing/process",
  "/business",
  "/process",
  "/templates/arbitration",
  "/templates/suit-filing",
]);

// Structural segments that are route prefixes, not standalone destinations
const structuralSegments = new Set([
  "variant", "job", "process", "business", "category",
]);

// Helper to capitalize and format segment
function formatSegment(segment: string): string {
  return segment
    .replace(/-/g, ' ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

// Helper to detect UUID format
function isUuid(str: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
}

/**
 * Determines if a breadcrumb path should be a clickable link.
 * Returns false for intermediate paths that have no route (would 404).
 */
function isNavigable(path: string, segments: string[], index: number): boolean {
  // Last segment is always the current page (rendered as text, not a link)
  if (index === segments.length - 1) return false;
  // Explicitly non-navigable paths
  if (nonNavigablePaths.has(path)) return false;
  // Known routes are always navigable
  if (routeNames[path]) return true;
  // UUID followed by more segments without its own route (e.g., /letterhead-components/:id in /letterhead-components/:id/edit)
  if (isUuid(segments[index]) && !routeNames[path]) return false;
  // Structural keywords that are just path prefixes (e.g., "variant" in .../variant/:variantId)
  if (structuralSegments.has(segments[index]) && !routeNames[path]) return false;
  return true;
}

interface BreadcrumbItemData {
  path: string;
  name: string;
  isLast: boolean;
  isEllipsis?: boolean;
  navigable: boolean;
}

export function Breadcrumbs() {
  const location = useLocation();
  const { labels } = useNavigation();
  const pathnames = location.pathname.split("/").filter((x) => x);

  // Don't show breadcrumbs on home page
  if (location.pathname === "/") {
    return null;
  }

  // Build breadcrumb items
  const breadcrumbItems: BreadcrumbItemData[] = pathnames.map((segment, index) => {
    const path = `/${pathnames.slice(0, index + 1).join("/")}`;
    const isLast = index === pathnames.length - 1;
    const navigable = isNavigable(path, pathnames, index);

    // Priority: static routes > dynamic labels > pattern labels > formatted segment
    let name = routeNames[path];

    if (!name) {
      // Check dynamic labels from context (for UUIDs/slugs)
      name = labels[segment];
    }

    if (!name) {
      // Check pattern-based labels
      const patternFn = patternLabels[segment.toLowerCase()];
      if (patternFn) {
        name = patternFn(segment);
      }
    }

    if (!name) {
      // If it's a UUID and we don't have a label, show a placeholder
      if (isUuid(segment)) {
        name = "...";
      } else {
        // Format the raw segment
        name = formatSegment(segment);
      }
    }

    return { path, name, isLast, navigable };
  });

  // On mobile (>3 items), collapse middle items to show first + ellipsis + last 2
  const shouldCollapse = breadcrumbItems.length > 3;
  const mobileItems: BreadcrumbItemData[] = shouldCollapse
    ? [
        breadcrumbItems[0],
        { path: '', name: '...', isLast: false, isEllipsis: true, navigable: false },
        ...breadcrumbItems.slice(-2),
      ]
    : breadcrumbItems;

  return (
    <Breadcrumb className="mb-4">
      {/* Mobile-responsive wrapper with horizontal scroll */}
      <BreadcrumbList className="flex-nowrap overflow-x-auto scrollbar-hide text-xs sm:text-sm">
        {/* Home link */}
        <BreadcrumbItem className="shrink-0">
          <BreadcrumbLink asChild>
            <Link to="/" className="flex items-center gap-1">
              <Home className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="sr-only">Home</span>
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>

        {/* Mobile view - collapsed for deep paths */}
        <div className="contents sm:hidden">
          {mobileItems.map((item, index) => (
            <React.Fragment key={item.path || `ellipsis-${index}`}>
              <BreadcrumbSeparator className="shrink-0">
                <ChevronRight className="h-3 w-3" />
              </BreadcrumbSeparator>
              <BreadcrumbItem className="shrink-0">
                {item.isEllipsis ? (
                  <BreadcrumbEllipsis className="h-5 w-5" />
                ) : item.isLast || !item.navigable ? (
                  <BreadcrumbPage className="max-w-[120px] truncate">
                    {item.name}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={item.path} className="max-w-[80px] truncate block">
                      {item.name}
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </React.Fragment>
          ))}
        </div>

        {/* Desktop view - full breadcrumb trail */}
        <div className="hidden sm:contents">
          {breadcrumbItems.map((item) => (
            <React.Fragment key={item.path}>
              <BreadcrumbSeparator className="shrink-0">
                <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </BreadcrumbSeparator>
              <BreadcrumbItem className="shrink-0">
                {item.isLast || !item.navigable ? (
                  <BreadcrumbPage className="max-w-[200px] truncate">
                    {item.name}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link
                      to={item.path}
                      className="max-w-[150px] truncate block hover:max-w-none transition-all"
                    >
                      {item.name}
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </React.Fragment>
          ))}
        </div>
      </BreadcrumbList>
    </Breadcrumb>
  );
}
